// Treat offsets within ~1 line height as effectively "at tail".
// This absorbs sub-pixel/resize jitter from composer growth and sticky footers.
// If chat chrome density changes substantially, this tolerance may need retuning.
export const SHARED_TIMELINE_BOTTOM_TOLERANCE_PX = 24

// How far the user must scroll away from the bottom before the "Jump to latest"
// button becomes visible.  This is intentionally much larger than
// SHARED_TIMELINE_BOTTOM_TOLERANCE_PX so that trivial scrolls (accidental
// trackpad nudges, small layout shifts, content that barely overflows the
// viewport) don't flash the button.  ~150px is roughly 3-4 lines of chat
// text, which represents "the user deliberately scrolled up and may not
// realise there's newer content below."  If the total scrollable overflow is
// less than this value, the button never appears — there's nothing
// meaningful to jump to.
export const JUMP_TO_LATEST_VISIBILITY_THRESHOLD_PX = 150

// Mobile session pages float the composer over the scroll root. The route writes
// the measured composer stack height into this CSS variable so timeline affordances
// that are portaled into the scroll root can clear the obstruction without
// guessing at prompt/editor/TodoDock height.
export const SHARED_TIMELINE_BOTTOM_OBSTRUCTION_CSS_VAR = "--shared-timeline-bottom-obstruction-px"

// Keep floating timeline affordances one standard spacing unit above the bottom
// obstruction. 16px matches Tailwind's `bottom-4` desktop placement; if the chat
// density scale changes, keep this paired with the visual gap used for desktop.
export const SHARED_TIMELINE_FLOATING_AFFORDANCE_GAP_PX = 16

type SharedTimelineJumpToLatestVisibilityArgs = {
  scrollHeightPx: number
  scrollTopPx: number
  clientHeightPx: number
}

// Treat fully-contained transcripts as already being at the live tail so a
// stale visibility state from an earlier taller layout does not leave the
// affordance floating over short threads. This is especially important when
// agent-activity groups collapse/expand without a user scroll event.
export function shouldShowSharedTimelineJumpToLatest({
  scrollHeightPx,
  scrollTopPx,
  clientHeightPx,
}: SharedTimelineJumpToLatestVisibilityArgs): boolean {
  const scrollableOverflowPx = Math.max(0, scrollHeightPx - clientHeightPx)
  if (scrollableOverflowPx <= SHARED_TIMELINE_BOTTOM_TOLERANCE_PX) return false

  const distanceFromBottomPx = Math.max(0, scrollHeightPx - scrollTopPx - clientHeightPx)
  return distanceFromBottomPx > JUMP_TO_LATEST_VISIBILITY_THRESHOLD_PX
}

// Revisit restore should be more forgiving than live auto-scroll stickiness.
// If the user left the thread within the same distance where we still hide the
// "Jump to latest" affordance, treat that as effectively being at the tail on
// switch-back / reload so catchup can resume from the bottom.  If this feels
// too sticky in practice, tighten it toward SHARED_TIMELINE_BOTTOM_TOLERANCE_PX.
export const SHARED_TIMELINE_PERSISTED_TAIL_RESTORE_THRESHOLD_PX =
  JUMP_TO_LATEST_VISIBILITY_THRESHOLD_PX

export const SHARED_TIMELINE_VIEWPORT_FLUSH_EVENT = "cord:shared-timeline-viewport-flush"

export type SharedTimelineTailScrollRequestReason =
  | "pr_footer_operation_started"
  | "merged_pr_banner_shown"
  | "jump_to_latest"

export interface SharedTimelineTailScrollRequestDetail {
  reason: SharedTimelineTailScrollRequestReason
  handled: boolean
}

export const SHARED_TIMELINE_TAIL_SCROLL_REQUEST_EVENT = "cord:shared-timeline-tail-scroll-request"

export function dispatchSharedTimelineViewportFlush(): void {
  if (typeof window === "undefined") return
  window.dispatchEvent(new Event(SHARED_TIMELINE_VIEWPORT_FLUSH_EVENT))
}

export function dispatchSharedTimelineTailScrollRequest(args: {
  reason: SharedTimelineTailScrollRequestReason
}): boolean {
  if (typeof window === "undefined") return false

  const detail: SharedTimelineTailScrollRequestDetail = {
    reason: args.reason,
    handled: false,
  }
  window.dispatchEvent(new CustomEvent(SHARED_TIMELINE_TAIL_SCROLL_REQUEST_EVENT, { detail }))
  return detail.handled
}
